import { filterBy } from '@progress/kendo-vue-data-tools';
import { FilterDescriptor, CompositeFilterDescriptor } from '@progress/kendo-data-query';
import { getter, setter } from '@progress/kendo-vue-common';

export const getValueMap = <T>(
  value: T[] | undefined,
  idGetter: (item: T) => string | number
): Record<string | number, boolean> => {
  const map: Record<string | number, boolean> = {};

  if (value && value.length) {
    value.forEach((item) => {
      map[idGetter(item)] = true;
    });
  }

  return map;
};

export const expandedState = <T extends object>(
  item: T,
  dataItemKey: string,
  expanded: Array<string | number>
): Array<string | number> => {
  const nextExpanded = expanded.slice();
  const keyGetter = getter(dataItemKey);
  const itemKey = keyGetter(item) as string | number;
  const index = expanded.findIndex((currentKey) => currentKey === itemKey);
  index === -1 ? nextExpanded.push(itemKey) : nextExpanded.splice(index, 1);

  return nextExpanded;
};

interface MapMultiSelectTreeOptions<T> {
  keyGetter: (item: T) => string | number;
  subItemGetter: (item: T) => T[] | undefined;
  subItemSetter: (item: T, subItems: T[] | undefined) => void;
  checkSetter: (item: T, value: boolean) => void;
  expandedSetter: (item: T, value: boolean) => void;
  checkIndeterminateSetter: (item: T, value: boolean) => void;
  valueMap: Record<string | number, boolean>;
  expandedMap: Record<string | number, boolean>;
}

const mapMultiSelectTreeData = <T extends object>(
  data: T[] | undefined,
  options: MapMultiSelectTreeOptions<T>
): [T[], boolean] => {
  const {
    keyGetter,
    subItemGetter,
    subItemSetter,
    checkSetter,
    expandedSetter,
    checkIndeterminateSetter,
    valueMap,
    expandedMap
  } = options;

  if (!data || !data.length) {
    return [data ?? [], false];
  }

  let hasChecked = false;
  const newData = [...data].map((dataItem) => {
    const [children, hasCheckedChildren] = mapMultiSelectTreeData(
      subItemGetter(dataItem),
      options
    );

    const isChecked = valueMap[keyGetter(dataItem)];
    if (isChecked || hasCheckedChildren) {
      hasChecked = true;
    }

    const newItem = { ...dataItem };

    expandedSetter(newItem, expandedMap[keyGetter(newItem)]);
    subItemSetter(newItem, children);
    checkSetter(newItem, !!isChecked);
    checkIndeterminateSetter(newItem, !isChecked && hasCheckedChildren);

    return newItem;
  });

  return [newData, hasChecked];
};

interface ProcessMultiSelectTreeDataOptions<T> {
  subItemsField?: string;
  checkField?: string;
  checkIndeterminateField?: string;
  expandField?: string;
  dataItemKey: string;
  value: T[] | undefined;
  filter?: FilterDescriptor | CompositeFilterDescriptor;
  expanded: Array<string | number>;
}

export const processMultiSelectTreeData = <T extends object>(
  tree: T[],
  options: ProcessMultiSelectTreeDataOptions<T>
): T[] => {
  const {
    subItemsField = 'items',
    checkField = 'checkField',
    checkIndeterminateField = 'checkIndeterminateField',
    expandField = 'expanded',
    dataItemKey,
    value,
    filter,
    expanded
  } = options;

  const keyGetter = getter(dataItemKey);
  const expandedMap: Record<string | number, boolean> = {};

  expanded.forEach((id) => {
    expandedMap[id] = true;
  });

  const [result] = mapMultiSelectTreeData(
    tree,
    {
      valueMap: getValueMap(value, keyGetter),
      expandedMap,
      keyGetter,
      expandedSetter: setter(expandField),
      subItemGetter: getter(subItemsField),
      subItemSetter: setter(subItemsField),
      checkSetter: setter(checkField),
      checkIndeterminateSetter: setter(checkIndeterminateField)
    }
  );

  if (filter && 'value' in filter && filter.value) {
    return filterBy(result, [filter as FilterDescriptor], subItemsField);
  }

  return result;
};